import { ChangeEvent, ReactElement, HTMLInputTypeAttribute, Ref, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { bouTextFontStyle, getTextStyle, textBaseStyle } from '../../../content/texts/bou-text/text-style';
import { BouColor } from '../../../global/bou-colors';
import { fluidSpaceLg } from '../../../utilities/fluid/fluid';

interface Props {
  value: string | undefined;
  placeholder: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  label?: string | ReactElement;
  setRef?: (element: Ref<any>) => void;
  className?: string;
  invalid?: boolean;
  invalidText?: string; // Pass an empty string to not show the text at all
  validateOnBlur?: boolean;
  type?: HTMLInputTypeAttribute;
  id?: string;
}

// The Underline drawing logic can cause performance issues in more complex settings, so this component
// should only be used in Facade, where things are simple.
export const BouTextInputFacade = (props: Props) => {
  const [underlineWidth, setUnderlineWidth] = useState(0);
  const [blurred, setBlurred] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (props.setRef) {
    props.setRef(inputRef);
  }

  const value = props.value || '';

  useEffect(() => {
    const current = inputRef.current;
    if (!current || !context) {
      return;
    }

    // The font properties used for the input text are checked from DOM, and an
    // invisible dummy text with similar values is measured with HTML canvas to check
    // its length. NOTE: probably will not work correctly with letter-spacing, as it is not a property
    // of the css 'font' -shorthand.
    const fontProperties = ['font-style', 'font-variant', 'font-weight', 'font-size', 'font-family'];
    context.font = fontProperties
      .map(property => `${window.getComputedStyle(current).getPropertyValue(property)}`)
      .join(' ');

    const textToMeasure = value.length ? value : undefined;
    if (textToMeasure) {
      const width = context.measureText(textToMeasure).width;
      setUnderlineWidth(width);
    } else {
      setUnderlineWidth(0);
    }
  }, [props.value, context, value]);

  const invalidText = props.invalidText !== undefined ? props.invalidText : 'Invalid value!';
  const showInvalid = !!props.invalid && (props.validateOnBlur ? blurred : true);

  return (
    <div>
      <Root className={props.className}>
        {props.label && typeof props.label === 'string' ? <StyledLabel>{props.label}</StyledLabel> : props.label}
        <StyledInput
          id={props.id}
          ref={inputRef}
          onBlur={() => {
            if (props.onBlur) {
              props.onBlur();
            }
            setBlurred(true);
          }}
          onFocus={props.onFocus}
          value={value}
          onChange={props.onChange}
          placeholder={props.placeholder}
          invalid={showInvalid}
          type={props.type}
        />
        {value && <Underline underlineWidth={underlineWidth} invalid={showInvalid} />}
      </Root>
      {showInvalid && <InvalidText>{invalidText}</InvalidText>}
    </div>
  );
};

const Root = styled.div`
  ${bouTextFontStyle};
  & + & {
    ${fluidSpaceLg('margin-top', 10)}
  }
  position: relative;
`;

const StyledInput = styled.input<{ invalid?: boolean }>`
  border-bottom: 1px solid rgba(0, 0, 0, 0.15);
  color: ${props => (!props.invalid ? BouColor.BLACK : BouColor.TOOL_TEXT_ERROR_RED)};
  background-color: transparent;
  width: 100%;
  max-width: 100%;
  &::placeholder {
    color: ${BouColor.DARK_GRAY_ALPHA};
  }
  margin-bottom: 1px;
`;

export const StyledLabel = styled.label`
  ${textBaseStyle}
  color: ${BouColor.BLACK};
  display: inline-block;
  margin-bottom: 0.5em;
`;

const Underline = styled.div<{ underlineWidth: number; invalid?: boolean }>`
  width: ${props => props.underlineWidth + 3}px;
  border-top: 1px solid ${props => (!props.invalid ? BouColor.BLACK : BouColor.TOOL_TEXT_ERROR_RED)};
  height: 1px;
  position: absolute;
  bottom: 1px;
  max-width: 100%;
`;

export const InvalidText = styled.div`
  ${getTextStyle({ size: 'xxs' })}
  color: ${BouColor.TOOL_TEXT_ERROR_RED};
  font-style: italic;
  margin-top: 0.3em;
`;
